import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { marketplaceApi, type Marketplace } from "../lib/api";
import {
  PlusIcon,
  TrashIcon,
  EditIcon,
  LinkIcon,
  CheckIcon,
  CopyIcon,
  ArrowLeftIcon,
} from "../components/ui/Icons";

export function MarketplaceDashboardPage() {
  const [marketplaces, setMarketplaces] = useState<Marketplace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadMarketplaces = async () => {
    try {
      const data = await marketplaceApi.getUserMarketplaces();
      setMarketplaces(data);
    } catch (err) {
      console.error("Failed to load marketplaces:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMarketplaces();
  }, []);

  const handleCreate = async () => {
    if (!formName.trim()) return;
    setIsSaving(true);
    try {
      await marketplaceApi.createMarketplace({
        name: formName.trim(),
        description: formDescription.trim() || undefined,
      });
      setFormName("");
      setFormDescription("");
      setShowCreateForm(false);
      await loadMarketplaces();
    } catch (err) {
      console.error("Failed to create marketplace:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (id: string) => {
    if (!formName.trim()) return;
    setIsSaving(true);
    try {
      await marketplaceApi.updateMarketplace(id, {
        name: formName.trim(),
        description: formDescription.trim() || null,
      });
      setEditingId(null);
      setFormName("");
      setFormDescription("");
      await loadMarketplaces();
    } catch (err) {
      console.error("Failed to update marketplace:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await marketplaceApi.deleteMarketplace(id);
      setDeleteId(null);
      await loadMarketplaces();
    } catch (err) {
      console.error("Failed to delete marketplace:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const startEdit = (marketplace: Marketplace) => {
    setEditingId(marketplace.id);
    setFormName(marketplace.name);
    setFormDescription(marketplace.description || "");
    setShowCreateForm(false);
  };

  const copyUrl = (slug: string, id: string) => {
    const url = `${window.location.origin}/m/${slug}`;
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="min-h-screen bg-base">
      <Header />

      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-20" />
      </div>

      <main className="relative pt-24">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
          {/* Back to Dashboard */}
          <Link
            to="/dashboard"
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-secondary"
          >
            <ArrowLeftIcon size={16} />
            Back to Dashboard
          </Link>

          {/* Header */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold tracking-tight text-primary">
                My <span className="text-amber">Marketplaces</span>
              </h1>
              <p className="mt-2 text-muted">
                Create curated collections of plugins and share them with others
              </p>
            </div>
            <button
              onClick={() => {
                setShowCreateForm(true);
                setEditingId(null);
                setFormName("");
                setFormDescription("");
              }}
              className="btn-primary flex items-center gap-2 self-start"
            >
              <PlusIcon size={18} />
              New Marketplace
            </button>
          </div>

          {/* Create / Edit Form */}
          {(showCreateForm || editingId) && (
            <div className="card-glow mb-8 p-6">
              <h2 className="mb-4 font-display text-lg font-bold text-primary">
                {editingId ? "Edit Marketplace" : "Create New Marketplace"}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block font-mono text-sm text-secondary">Name</label>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="My Awesome Marketplace"
                    className="w-full rounded-lg border border-elevated bg-surface/50 px-4 py-2.5 font-body text-sm text-primary placeholder-muted outline-none transition-colors focus:border-amber/50 focus:ring-1 focus:ring-amber/20"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="mb-1 block font-mono text-sm text-secondary">
                    Description (optional)
                  </label>
                  <textarea
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="A curated collection of plugins for..."
                    rows={3}
                    className="w-full rounded-lg border border-elevated bg-surface/50 px-4 py-2.5 font-body text-sm text-primary placeholder-muted outline-none transition-colors focus:border-amber/50 focus:ring-1 focus:ring-amber/20"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => (editingId ? handleUpdate(editingId) : handleCreate())}
                    disabled={!formName.trim() || isSaving}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  >
                    {isSaving ? "Saving..." : editingId ? "Save Changes" : "Create Marketplace"}
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingId(null);
                      setFormName("");
                      setFormDescription("");
                    }}
                    className="rounded-lg px-4 py-2 text-sm text-muted transition-colors hover:text-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Marketplace List */}
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card-glow animate-pulse p-6">
                  <div className="mb-3 h-5 w-40 rounded bg-elevated/50" />
                  <div className="mb-4 h-3 w-full rounded bg-elevated/50" />
                  <div className="h-4 w-20 rounded bg-elevated/50" />
                </div>
              ))}
            </div>
          ) : marketplaces.length === 0 ? (
            <div className="card-glow flex flex-col items-center justify-center py-20 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-elevated/50">
                <LinkIcon size={28} className="text-muted" />
              </div>
              <h3 className="mb-2 font-display text-lg font-bold text-primary">
                No marketplaces yet
              </h3>
              <p className="mb-6 max-w-sm text-sm text-muted">
                Create your first marketplace to curate a collection of plugins and share it with
                others via a unique link.
              </p>
              <button
                onClick={() => {
                  setShowCreateForm(true);
                  setFormName("");
                  setFormDescription("");
                }}
                className="btn-primary flex items-center gap-2"
              >
                <PlusIcon size={18} />
                Create Your First Marketplace
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {marketplaces.map((marketplace) => (
                <div key={marketplace.id} className="card-glow p-6">
                  <div className="mb-3 flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-display text-base font-bold text-primary">
                        {marketplace.name}
                      </h3>
                      {marketplace.description && (
                        <p className="mt-1 line-clamp-2 text-sm text-muted">
                          {marketplace.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="mb-4 flex items-center gap-4">
                    <span className="font-mono text-sm text-muted">
                      {marketplace._count.plugins}{" "}
                      {marketplace._count.plugins === 1 ? "plugin" : "plugins"}
                    </span>
                  </div>

                  {/* Shareable URL */}
                  <div className="mb-4 flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded bg-elevated/50 px-3 py-1.5 font-mono text-xs text-muted">
                      /m/{marketplace.slug}
                    </code>
                    <button
                      onClick={() => copyUrl(marketplace.slug, marketplace.id)}
                      className="flex-shrink-0 rounded-lg p-2 text-muted transition-colors hover:bg-elevated hover:text-secondary"
                      title="Copy link"
                    >
                      {copiedId === marketplace.id ? (
                        <CheckIcon size={16} className="text-success" />
                      ) : (
                        <CopyIcon size={16} />
                      )}
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/dashboard/marketplaces/${marketplace.id}/edit`}
                      className="btn-primary flex-1 py-2 text-center text-sm"
                    >
                      Manage Plugins
                    </Link>
                    <button
                      onClick={() => startEdit(marketplace)}
                      className="rounded-lg p-2 text-muted transition-colors hover:bg-elevated hover:text-secondary"
                      title="Edit details"
                    >
                      <EditIcon size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteId(marketplace.id)}
                      className="rounded-lg p-2 text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                      title="Delete"
                    >
                      <TrashIcon size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteId && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-base/80 backdrop-blur-sm">
              <div className="card-glow mx-4 max-w-sm p-6">
                <h3 className="mb-2 font-display text-lg font-bold text-primary">
                  Delete Marketplace?
                </h3>
                <p className="mb-6 text-sm text-muted">
                  This will permanently delete this marketplace and all its plugin selections. This
                  cannot be undone.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleDelete(deleteId)}
                    disabled={isDeleting}
                    className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-danger/80 disabled:opacity-50"
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                  <button
                    onClick={() => setDeleteId(null)}
                    className="rounded-lg px-4 py-2 text-sm text-muted transition-colors hover:text-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
